import React from 'react';
import {Image, StyleSheet, Text, View} from '@react-pdf/renderer';
import {getValidDateTimeForEvent, getValidPeriodSeasonalPass} from "./utils"
import {getTranslationByLocale} from "../utils/getCheckoutFromApiData";
import {useTranslation} from "react-i18next";

const styles = StyleSheet.create({
    pass: {
        flexDirection: "column",
        border: "6pt solid #B5B5B5",
        borderRadius: 10,
        maxWidth: "81mm",
        backgroundColor: '#f1f0f0',
        maxHeight: "127.5mm",
    },
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        padding: 10
    },
    logo: {
        flexBasis: "50%",
        padding: 5,
    },
    ticketCode: {
        flexDirection: "row",
        padding: 5,
        alignSelf: 'center',
        fontSize: 10,
        color:'grey',
    },
    logoText: {
        fontSize: 12,
        fontWeight: "bold"
    },
    date: {

    },
    body: {

    },
    footer: {
        alignItems: "center",
        justifyContent: "center"
    },
    qrCode: {
        width: 80,
        height: 80,
        border: "2pt dotted black",
    },
    qrCodeFrame: {
        border: "4pt solid black",
        margin: 10,
    },
    primary: {
        borderBottom: "2pt dotted black",
        borderColor: "black",
        padding: 5,
    },
    secondary: {
        padding: 5,
    },

    row: {
        flexDirection: "row",
        justifyContent: "space-between",
        margin: 5,
        flexWrap: "wrap"
    },
    nameRow: {
        justifyContent: "space-between",
        margin: 5,
        flexDirection: "row",
    },
    label: {
        fontSize: 12,
        fontWeight: "normal",
        opacity: .85
    },
    value: {
        fontSize: 12,
    },
    title: {
        fontSize: 14
    },
    nameLabel: {
        fontSize: 14,
        maxWidth: "75%"
    },
    testTicket: {
        fontSize: 12,
        color: "red",
        alignSelf: 'center',
    },
    museumImage: {
        width: '40px',
        maxHeight: '60px'
    }
});

const englishStaticLabels = {
    name: "Name",
    title: "Title",
    category: "Category",
    price: "Price",
    location: "Location",
    valid: "Valid",
    time: "Time",
    test: "Test ticket - not valid"
}

const norwegianStaticLabels = {
    name: "Navn",
    title: "Tittel",
    category: "Kategori",
    price: "Pris",
    location: "Sted",
    valid: "Gyldig",
    time: "Tidspunkt",
    test: "Testbillett - ikke gyldig"
}

const getPdfLabelsByLocale = (locale) => {
    if (!locale || locale === "no" || locale === "nn" || locale === "nb") {
        return  norwegianStaticLabels
    } else {
        return englishStaticLabels
    }
}

const getTranslatedPriceLabelByIdAndLocale = (productPriceCategoryId, locale, product) => {
    const price = product.prices.find(price => price.productPriceCategoryId === productPriceCategoryId)
    if (price) {
        let priceLabel = price.name
        if(price.translations.length > 0) {
            price.translations.forEach(translation => {
                if (translation.languageLocale.toLowerCase() === locale.toLowerCase()) {
                    priceLabel =  translation.name
                }
            })
        }
        return priceLabel
    } else {
        return null
    }
}


const PdfPass = (props) => {
    const {code, orderLine, test, order, product, imageUrl} = props
    const { t } = useTranslation('portal')
    const priceFormatted = (orderLine.price && "Kr. " + orderLine.price + ",-") || t('priceLabelFree')
    const timeForEvent = orderLine.productType !== "seasonal_pass" ? getValidDateTimeForEvent(orderLine) : getValidPeriodSeasonalPass(product, order)
    const qrCodeUrl = "//api.qrserver.com/v1/create-qr-code/?color=000000&bgcolor=FFFFFF&qzone=1&margin=0&size=400x400&ecc=L&data=" + code
    const firstName = order.giftedToFirstName || order.customer.firstName
    const lastName = order.giftedToLastName || order.customer.lastName
    const locale = localStorage.getItem("i18nextLng")
    let staticLabels = getPdfLabelsByLocale(locale);

    return (
        <View style={styles.pass}>
            <View>
                <Text style={styles.ticketCode}>{code}</Text>
            </View>
            <View style={styles.primary}>
                <View style={styles.nameRow}>
                    <View>
                        <Text style={styles.label}>{staticLabels.name}</Text>
                        <Text style={styles.nameLabel}>{`${firstName.charAt(0).toUpperCase() + firstName.slice(1)}\n${lastName.charAt(0).toUpperCase() + lastName.slice(1)}`}</Text>
                    </View>
                    <View>
                        <Image style={styles.museumImage} src={imageUrl || "data:image/png;base64,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"}/>
                    </View>
                </View>
                <View style={styles.row}>
                    <View>
                        <Text style={styles.label}>{staticLabels.title}</Text>
                        <Text style={styles.title}>{getTranslationByLocale(product, "name", locale)}</Text>
                    </View>
                </View>
                <View style={styles.row}>
                    <View>
                        <Text style={styles.label}>{staticLabels.category}</Text>
                        <Text style={styles.title}>{getTranslatedPriceLabelByIdAndLocale(orderLine.productPriceCategoryId, locale, product) || orderLine.name}</Text>
                    </View>
                    <View>
                        <Text style={styles.label}>{staticLabels.price}</Text>
                        <Text style={styles.title}>{priceFormatted}</Text>
                    </View>
                </View>
            </View>
            <View style={styles.secondary}>
                <View style={styles.row}>
                    <View>
                        <Text style={styles.label}>{staticLabels.location}</Text>
                        <Text style={styles.value}>{orderLine.museumName}</Text>
                    </View>
                </View>
                {timeForEvent !== '' && <View style={styles.row}>
                    <View>
                        <Text style={styles.label}>{orderLine.productType !== "seasonal_pass" ? staticLabels.time : staticLabels.valid}</Text>
                        <Text style={styles.value}>{timeForEvent}</Text>
                    </View>
                </View>}
            </View>
            { test &&
                <View>
                    <Text style={styles.testTicket}>{staticLabels.test}</Text>
                </View>
            }
            <View style={styles.footer}>
                <View style={styles.qrCodeFrame}>
                    <Image src={qrCodeUrl} style={styles.qrCode} />
                </View>
            </View>
        </View>
    )
}


export default PdfPass
